import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

export const fetchTests = createServerFn({ method: "POST" }).handler(async () => {
  const { requireProfile } = await import("./session.server");
  const { listTests } = await import("./tests.server");
  const profile = await requireProfile();
  return listTests(profile.id);
});

export const createTest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        name: z.string().trim().min(1).max(80),
        testDate: dateSchema,
        today: dateSchema,
        chapters: z
          .array(
            z.object({
              subject: z.string().trim().min(1).max(40),
              chapter: z.string().trim().min(1).max(120),
              weak: z.boolean(),
            }),
          )
          .min(1)
          .max(40),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { assertWithinQuota } = await import("./quota.server");
    const { createTestPlan } = await import("./tests.server");
    const profile = await requireProfile();
    await assertWithinQuota(profile.id, "tests");
    return createTestPlan(profile.id, data);
  });

export const toggleTestItem = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ itemId: z.string().uuid(), completed: z.boolean(), today: dateSchema })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { setTestItemStatus } = await import("./tests.server");
    const profile = await requireProfile();
    await setTestItemStatus(profile.id, data.itemId, data.completed, data.today);
    return { ok: true };
  });

export const addTestChapters = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        testId: z.string().uuid(),
        today: dateSchema,
        chapters: z
          .array(
            z.object({
              subject: z.string().trim().min(1).max(40),
              chapter: z.string().trim().min(1).max(120),
              weak: z.boolean(),
            }),
          )
          .min(1)
          .max(40),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { addChaptersToTest } = await import("./tests.server");
    const profile = await requireProfile();
    return addChaptersToTest(profile.id, data);
  });

export const removeTest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => z.object({ testId: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { deleteTest } = await import("./tests.server");
    const profile = await requireProfile();
    await deleteTest(profile.id, data.testId);
    return { ok: true };
  });

const marks = z.number().min(0).max(1000).nullable();

export const saveTestScoreFn = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        testId: z.string().uuid(),
        today: dateSchema,
        obtained: z.number().min(0).max(1000),
        total: z.number().min(1).max(1000),
        physics: marks,
        chemistry: marks,
        maths: marks,
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { saveTestScore } = await import("./tests.server");
    const profile = await requireProfile();
    await saveTestScore(profile.id, data);
    return { ok: true };
  });

export const updateTest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        testId: z.string().uuid(),
        name: z.string().trim().min(1).max(80).optional(),
        testDate: dateSchema.optional(),
        scoreUnlocked: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { updateTestDetails } = await import("./tests.server");
    const profile = await requireProfile();
    await updateTestDetails(profile.id, data);
    return { ok: true };
  });
